import { useMutation } from "@apollo/client";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { SafeDialog } from "components/Dialog";
import { VALID_ENCAMP_STATUSES } from "encamp-shared/src/submissions";
import { SubmissionEncampStatus } from "generated-graphql/graphql";
import { BULK_UPDATE_SUBMISSION_ENCAMP_STATUS_MUTATION } from "queries/submissions";
import { useState } from "react";

import { getStatusDisplay, getEncampStatusDisplay } from "./SubmissionStatusChipWithPopover";

interface ChangeEncampStatusSubmission {
  submissionId: string;
  submissionStatus: string;
}

interface ChangeEncampStatusDialogProps {
  open: boolean;
  onClose: () => void;
  submissions: ChangeEncampStatusSubmission[];
  onSuccess?: () => void;
}

export function ChangeEncampStatusDialog({
  open,
  onClose,
  submissions,
  onSuccess,
}: ChangeEncampStatusDialogProps) {
  const alerts = useAlerts();
  const [selectedEncampStatus, setSelectedEncampStatus] = useState<
    SubmissionEncampStatus | "CLEAR" | ""
  >("");

  const commonStatus = submissions.length > 0 ? submissions[0].submissionStatus : null;
  const validSubStatuses = commonStatus ? (VALID_ENCAMP_STATUSES[commonStatus] ?? []) : [];
  const submissionIds = submissions.map((s) => s.submissionId);

  const [bulkUpdate, { loading }] = useMutation(BULK_UPDATE_SUBMISSION_ENCAMP_STATUS_MUTATION, {
    refetchQueries: [
      "WasteSubmissionsForStaff",
      "WasteSubmissionsStaffSummary",
      "SubmissionVirtualEvents",
    ],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      const result = data.bulkUpdateSubmissionEncampStatus;
      if (result.succeeded) {
        alerts.success(`Updated encamp status for ${result.updated} submission(s).`);
      } else {
        alerts.warning(`Updated ${result.updated} submission(s), ${result.failed} failed.`);
      }
      handleClose();
      onSuccess?.();
    },
    onError: (error) => {
      alerts.error(`Failed to update encamp status: ${error.message}`);
    },
  });

  const handleClose = () => {
    setSelectedEncampStatus("");
    onClose();
  };

  const handleChange = (event: SelectChangeEvent<string>) => {
    setSelectedEncampStatus(event.target.value as SubmissionEncampStatus | "CLEAR");
  };

  const handleSubmit = async () => {
    if (!selectedEncampStatus) return;
    const encampStatus = selectedEncampStatus === "CLEAR" ? null : selectedEncampStatus;
    await bulkUpdate({
      variables: { submissionIds, encampStatus },
    });
  };

  return (
    <SafeDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change Encamp Status for {submissions.length} Submission(s)</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {commonStatus && (
            <Typography variant="body2" color="text.secondary">
              All selected submissions are <strong>{getStatusDisplay(commonStatus)}</strong>. Choose
              an encamp status modifier to apply.
            </Typography>
          )}

          <FormControl fullWidth>
            <InputLabel id="encamp-status-select-label">Encamp Status *</InputLabel>
            <Select
              labelId="encamp-status-select-label"
              value={selectedEncampStatus}
              label="Encamp Status *"
              onChange={handleChange}
              disabled={loading}
            >
              <MenuItem value="CLEAR">
                <em>Clear (no modifier)</em>
              </MenuItem>
              {validSubStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {getEncampStatusDisplay(status)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedEncampStatus && (
            <Typography variant="body2" color="text.secondary">
              {selectedEncampStatus === "CLEAR"
                ? `This will clear the encamp status modifier from ${submissions.length} submission(s).`
                : `This will set ${submissions.length} submission(s) to "${getStatusDisplay(
                    commonStatus ?? ""
                  )} - ${getEncampStatusDisplay(selectedEncampStatus as SubmissionEncampStatus)}".`}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !selectedEncampStatus}
        >
          {loading ? "Updating..." : "Confirm"}
        </Button>
      </DialogActions>
    </SafeDialog>
  );
}
